import { siteConfig } from "../data/content";

const email = siteConfig.company.email;

export const translations = {
  // ==========================================================================
  // ENGLISH
  // ==========================================================================
  en: {
    nav: {
      home: "Home",
      about: "About",
      whatWeDo: "What We Do",
      investmentApproach: "Investment Approach",
      contact: "Contact",
      contactUs: "Contact Us",
    },
    hero: {
      overline: "Real Estate Investment & Development",
      headline1: "Creating Better Homes.",
      headline2: "Building Long-Term Value.",
      subtext:
        "M&K Properties is a Greece-based real estate investment and development company focused on acquiring, upgrading, and repositioning high-potential residential properties.",
      primary: "Discuss an Opportunity",
      secondary: "Learn More",
    },
    snapshot: {
      title: "Company Snapshot",
      items: [
        "Based in Greece",
        "Residential Real Estate",
        "Investment & Development",
        "Acquisitions",
        "Renovations",
        "Long-Term Rentals",
        "Value-Add Investments",
      ],
    },
    about: {
      overline: "About",
      title: "About M&K Properties",
      p1: "M&K Properties is a premium real estate investment and development company based in Greece. We identify high-potential residential properties, acquire them, renovate them to a high standard, and either resell them or retain them as long-term rental investments.",
      p2: "Our philosophy is straightforward: create modern, functional, and high-quality homes while generating sustainable long-term value for investors, clients, and local communities.",
      p3: "While our primary focus is Greece, we also evaluate opportunities in selected European markets where long-term residential demand is supported by strong fundamentals — including population stability, employment, accessibility, and constrained housing supply.",
      quote:
        "We combine disciplined property selection, thoughtful design, quality execution, and a long-term ownership mindset.",
    },
    why: {
      overline: "Our Purpose",
      title: "Why We Exist",
      p1: "A significant share of residential real estate is underutilized, outdated, or poorly configured for the way people live today. These properties often sit below their potential — for the people who could live in them and for the communities around them.",
      p2: "M&K Properties exists to close that gap. We acquire such properties, renovate them to a modern and functional standard, and steward them responsibly over the long term — creating better homes for occupants and durable, sustainable value for our partners.",
    },
    whatWeDo: {
      overline: "What We Do",
      title: "A disciplined residential investment lifecycle.",
      cards: [
        {
          title: "Property Sourcing",
          copy: "We identify residential properties with strong location fundamentals, improvement potential, and long-term market relevance.",
        },
        {
          title: "Acquisition",
          copy: "We evaluate and acquire selected opportunities through a disciplined, commercially focused investment process.",
        },
        {
          title: "Renovation & Development",
          copy: "We transform properties into modern, functional, and high-quality homes through carefully planned renovation and development.",
        },
        {
          title: "Resale & Long-Term Rental",
          copy: "We either reposition properties for resale or retain them as professionally managed long-term rental investments.",
        },
      ],
    },
    approach: {
      overline: "Investment Approach",
      title: "Our Investment Approach",
      principles: [
        {
          title: "Location First",
          copy: "We focus on areas with resilient demand, strong accessibility, and long-term potential.",
        },
        {
          title: "Value Creation",
          copy: "We seek opportunities where better planning, design, and execution can unlock meaningful value.",
        },
        {
          title: "Quality Without Excess",
          copy: "We prioritize durable materials, efficient layouts, and timeless design rather than short-lived trends.",
        },
        {
          title: "Long-Term Perspective",
          copy: "Every decision is assessed through the lens of sustainability, usability, and long-term value.",
        },
      ],
      processLabel: "Our Process",
      steps: [
        "Identify",
        "Evaluate",
        "Acquire",
        "Renovate",
        "Reposition",
        "Hold or Sell",
      ],
    },
    focus: {
      overline: "Investment Focus",
      title: "Residential assets we pursue.",
      items: [
        {
          title: "Urban Apartments",
          copy: "Thoughtfully upgraded homes in established and emerging urban locations.",
        },
        {
          title: "Residential Renovations",
          copy: "Modern interiors, improved layouts, and high-quality finishes designed for everyday living.",
        },
        {
          title: "Long-Term Rental Assets",
          copy: "Durable, functional properties positioned for stable long-term demand.",
        },
      ],
      note: "Project information will be added as the portfolio develops.",
    },
    opportunity: {
      overline: "Opportunities",
      title: "We Are Interested in the Right Opportunities",
      intro:
        "We are open to discussing residential properties, off-market opportunities, redevelopment projects, and strategic partnerships across Greece.",
      columns: [
        {
          title: "Property Owners",
          copy: "Considering selling a residential property? We are interested in evaluating suitable opportunities.",
        },
        {
          title: "Agents & Brokers",
          copy: "We welcome introductions to properties with renovation, repositioning, or investment potential.",
        },
        {
          title: "Investors & Partners",
          copy: "We are open to selected conversations with investors and professional partners who share our long-term approach.",
        },
      ],
      cta: "Contact M&K Properties",
    },
    trust: {
      overline: "Governance",
      title: "A Professional, Disciplined Approach",
      body: "M&K Properties operates with a strong focus on transparency, careful due diligence, responsible capital allocation, and professional collaboration with legal, technical, financial, and construction specialists.",
    },
    faq: {
      overline: "FAQ",
      title: "Frequently Asked Questions",
      items: [
        {
          q: "What types of properties do you acquire?",
          a: "We focus on residential real estate — primarily apartments and urban residential buildings — with sound location fundamentals and clear potential for improvement through renovation, better layouts, and repositioning.",
        },
        {
          q: "Do you work with agents and brokers?",
          a: "Yes. We welcome introductions from agents and brokers regarding residential properties, off-market opportunities, and redevelopment projects that align with our long-term, value-add approach.",
        },
        {
          q: "Do you invest outside Greece?",
          a: "Our primary focus is Greece. We also evaluate selected opportunities in other European markets where long-term residential demand is supported by strong fundamentals.",
        },
      ],
    },
    contact: {
      overline: "Contact",
      title: "Let's Discuss an Opportunity",
      intro:
        "Contact us regarding property opportunities, partnerships, or general business enquiries. We welcome introductions from property owners, agents, investors, and professional partners.",
      getInTouch: "Get in touch",
      emailUs: "Email Us",
    },
    common: { location: "Greece" },
    footer: {
      descriptor: "Real Estate Investment & Development",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      disclaimer:
        "The information on this website is provided for general informational purposes only and does not constitute financial, legal, tax, or investment advice.",
      copyright: "© 2026 M&K Properties. All rights reserved.",
    },
    legal: {
      back: "Back to home",
      legalLabel: "Legal",
      privacy: {
        title: "Privacy Policy",
        updated: "Last updated: 2026",
        intro:
          'M&K Properties ("we", "us", "our") respects your privacy. This policy explains, in general terms, how information submitted through this website is handled. It is provided for general informational purposes and should be reviewed by a qualified legal professional before publication.',
        sections: [
          { heading: "Information We Collect", body: "When you use our contact channels, we collect the details you choose to provide, such as your name, company, email address, phone number, reason for contact, and message." },
          { heading: "How We Use Your Information", body: "Your information is used solely to respond to your enquiry and to communicate with you regarding property opportunities, partnerships, or general business matters. We do not sell your information to third parties." },
          { heading: "Data Retention", body: "We retain enquiry information only for as long as necessary to respond to and manage your request, after which it may be deleted upon request." },
          { heading: "Your Rights", body: "You may request access to, correction of, or deletion of the personal information you have provided to us at any time by contacting us." },
          { heading: "Contact", body: `For any questions about this policy or to exercise your rights, please contact us at ${email}.` },
        ],
      },
      terms: {
        title: "Terms of Use",
        updated: "Last updated: 2026",
        intro:
          "These terms govern your use of the M&K Properties website. By accessing or using this website, you agree to these terms. This text is provided for general informational purposes and should be reviewed by a qualified legal professional before publication.",
        sections: [
          { heading: "No Advice", body: "The information on this website is provided for general informational purposes only and does not constitute financial, legal, tax, or investment advice, nor an offer or solicitation to buy or sell any asset." },
          { heading: "No Guarantees", body: "We make no representations or warranties regarding investment outcomes, returns, or the completeness or accuracy of the information presented on this website." },
          { heading: "Intellectual Property", body: "All content on this website, including text, layout, and branding, is the property of M&K Properties unless otherwise stated, and may not be reproduced without prior written permission." },
          { heading: "External Links", body: "This website may contain links to third-party websites. We are not responsible for the content, policies, or practices of any third-party websites." },
          { heading: "Governing Law", body: "These terms are governed by the laws of Greece, without regard to conflict-of-law principles." },
          { heading: "Contact", body: `For questions regarding these terms, please contact us at ${email}.` },
        ],
      },
    },
  },

  // ==========================================================================
  // BULGARIAN
  // ==========================================================================
  bg: {
    nav: {
      home: "Начало",
      about: "За нас",
      whatWeDo: "Какво правим",
      investmentApproach: "Инвестиционен подход",
      contact: "Контакти",
      contactUs: "Свържете се",
    },
    hero: {
      overline: "Инвестиции и развитие в недвижими имоти",
      headline1: "Създаваме по-добри домове.",
      headline2: "Изграждаме дългосрочна стойност.",
      subtext:
        "M&K Properties е компания за инвестиции и развитие на недвижими имоти със седалище в Гърция, фокусирана върху придобиването, обновяването и препозиционирането на жилищни имоти с висок потенциал.",
      primary: "Обсъдете възможност",
      secondary: "Научете повече",
    },
    snapshot: {
      title: "Профил на компанията",
      items: [
        "Седалище в Гърция",
        "Жилищни недвижими имоти",
        "Инвестиции и развитие",
        "Придобивания",
        "Обновявания",
        "Дългосрочни наеми",
        "Инвестиции с добавена стойност",
      ],
    },
    about: {
      overline: "За нас",
      title: "За M&K Properties",
      p1: "M&K Properties е компания за инвестиции и развитие на недвижими имоти от висок клас със седалище в Гърция. Идентифицираме жилищни имоти с висок потенциал, придобиваме ги, обновяваме ги до висок стандарт и след това ги препродаваме или задържаме като дългосрочни инвестиции под наем.",
      p2: "Нашата философия е ясна: да създаваме модерни, функционални и висококачествени домове, като същевременно генерираме устойчива дългосрочна стойност за инвеститори, клиенти и местни общности.",
      p3: "Макар че основният ни фокус е Гърция, ние оценяваме и възможности на избрани европейски пазари, където дългосрочното търсене на жилища се подкрепя от силни фундаментални фактори — включително стабилност на населението, заетост, достъпност и ограничено предлагане на жилища.",
      quote:
        "Съчетаваме дисциплиниран подбор на имоти, обмислен дизайн, качествено изпълнение и дългосрочна собственическа нагласа.",
    },
    why: {
      overline: "Нашата цел",
      title: "Защо съществуваме",
      p1: "Значителна част от жилищните имоти са недостатъчно използвани, остарели или лошо пригодени към начина, по който хората живеят днес. Тези имоти често остават под потенциала си — както за хората, които биха могли да живеят в тях, така и за общностите около тях.",
      p2: "M&K Properties съществува, за да преодолее тази разлика. Придобиваме такива имоти, обновяваме ги до модерен и функционален стандарт и ги стопанисваме отговорно в дългосрочен план — създавайки по-добри домове за обитателите и трайна, устойчива стойност за нашите партньори.",
    },
    whatWeDo: {
      overline: "Какво правим",
      title: "Дисциплиниран жизнен цикъл на жилищни инвестиции.",
      cards: [
        { title: "Намиране на имоти", copy: "Идентифицираме жилищни имоти със силни локационни характеристики, потенциал за подобрение и дългосрочна пазарна значимост." },
        { title: "Придобиване", copy: "Оценяваме и придобиваме избрани възможности чрез дисциплиниран, търговски ориентиран инвестиционен процес." },
        { title: "Обновяване и развитие", copy: "Преобразяваме имотите в модерни, функционални и висококачествени домове чрез внимателно планирано обновяване и развитие." },
        { title: "Препродажба и дългосрочен наем", copy: "Препозиционираме имотите за препродажба или ги задържаме като професионално управлявани дългосрочни инвестиции под наем." },
      ],
    },
    approach: {
      overline: "Инвестиционен подход",
      title: "Нашият инвестиционен подход",
      principles: [
        { title: "Локацията на първо място", copy: "Фокусираме се върху райони с устойчиво търсене, добра достъпност и дългосрочен потенциал." },
        { title: "Създаване на стойност", copy: "Търсим възможности, при които по-доброто планиране, дизайн и изпълнение могат да отключат значима стойност." },
        { title: "Качество без излишества", copy: "Даваме приоритет на трайни материали, ефективни разпределения и вечен дизайн вместо краткотрайни тенденции." },
        { title: "Дългосрочна перспектива", copy: "Всяко решение се оценява през призмата на устойчивостта, използваемостта и дългосрочната стойност." },
      ],
      processLabel: "Нашият процес",
      steps: ["Идентифициране", "Оценка", "Придобиване", "Обновяване", "Препозициониране", "Задържане или продажба"],
    },
    focus: {
      overline: "Инвестиционен фокус",
      title: "Жилищни активи, върху които се фокусираме.",
      items: [
        { title: "Градски апартаменти", copy: "Внимателно обновени домове в утвърдени и развиващи се градски локации." },
        { title: "Жилищни ремонти", copy: "Модерни интериори, подобрени разпределения и висококачествени довършителни работи за ежедневния живот." },
        { title: "Активи за дългосрочен наем", copy: "Трайни, функционални имоти, позиционирани за стабилно дългосрочно търсене." },
      ],
      note: "Информация за проектите ще бъде добавяна с развитието на портфолиото.",
    },
    opportunity: {
      overline: "Възможности",
      title: "Търсим правилните възможности",
      intro:
        "Отворени сме да обсъдим жилищни имоти, извънпазарни възможности, проекти за преустройство и стратегически партньорства в цяла Гърция.",
      columns: [
        { title: "Собственици на имоти", copy: "Обмисляте продажба на жилищен имот? Заинтересовани сме да оценим подходящи възможности." },
        { title: "Агенти и брокери", copy: "Приветстваме представянето на имоти с потенциал за обновяване, препозициониране или инвестиция." },
        { title: "Инвеститори и партньори", copy: "Отворени сме за избрани разговори с инвеститори и професионални партньори, които споделят нашия дългосрочен подход." },
      ],
      cta: "Свържете се с M&K Properties",
    },
    trust: {
      overline: "Управление",
      title: "Професионален, дисциплиниран подход",
      body: "M&K Properties работи със силен фокус върху прозрачността, внимателната надлежна проверка, отговорното разпределение на капитала и професионалното сътрудничество с юридически, технически, финансови и строителни специалисти.",
    },
    faq: {
      overline: "Често задавани въпроси",
      title: "Често задавани въпроси",
      items: [
        { q: "Какви видове имоти придобивате?", a: "Фокусираме се върху жилищни имоти — предимно апартаменти и градски жилищни сгради — със стабилни локационни характеристики и ясен потенциал за подобрение чрез обновяване, по-добри разпределения и препозициониране." },
        { q: "Работите ли с агенти и брокери?", a: "Да. Приветстваме представяния от агенти и брокери относно жилищни имоти, извънпазарни възможности и проекти за преустройство, които съответстват на нашия дългосрочен подход с добавена стойност." },
        { q: "Инвестирате ли извън Гърция?", a: "Основният ни фокус е Гърция. Оценяваме и избрани възможности на други европейски пазари, където дългосрочното търсене на жилища се подкрепя от силни фундаментални фактори." },
      ],
    },
    contact: {
      overline: "Контакти",
      title: "Нека обсъдим възможност",
      intro:
        "Свържете се с нас относно възможности за имоти, партньорства или общи бизнес запитвания. Приветстваме представяния от собственици на имоти, агенти, инвеститори и професионални партньори.",
      getInTouch: "Свържете се с нас",
      emailUs: "Изпратете ни имейл",
    },
    common: { location: "Гърция" },
    footer: {
      descriptor: "Инвестиции и развитие в недвижими имоти",
      privacy: "Политика за поверителност",
      terms: "Условия за ползване",
      disclaimer:
        "Информацията на този уебсайт е предоставена само с обща информационна цел и не представлява финансов, правен, данъчен или инвестиционен съвет.",
      copyright: "© 2026 M&K Properties. Всички права запазени.",
    },
    legal: {
      back: "Обратно към началото",
      legalLabel: "Правна информация",
      privacy: {
        title: "Политика за поверителност",
        updated: "Последна актуализация: 2026",
        intro:
          "M&K Properties („ние“, „нас“, „нашият“) зачита вашата поверителност. Тази политика обяснява в общи линии как се обработва информацията, подадена чрез този уебсайт. Тя е предоставена с обща информационна цел и трябва да бъде прегледана от квалифициран юрист преди публикуване.",
        sections: [
          { heading: "Каква информация събираме", body: "Когато използвате каналите ни за контакт, събираме данните, които решите да предоставите, като вашето име, компания, имейл адрес, телефонен номер, причина за контакт и съобщение." },
          { heading: "Как използваме вашата информация", body: "Вашата информация се използва единствено за да отговорим на вашето запитване и да общуваме с вас относно възможности за имоти, партньорства или общи бизнес въпроси. Не продаваме вашата информация на трети страни." },
          { heading: "Съхранение на данни", body: "Съхраняваме информацията от запитванията само толкова дълго, колкото е необходимо, за да отговорим и обработим вашето искане, след което тя може да бъде изтрита при поискване." },
          { heading: "Вашите права", body: "Можете да поискате достъп, коригиране или изтриване на предоставената от вас лична информация по всяко време, като се свържете с нас." },
          { heading: "Контакт", body: `За въпроси относно тази политика или за да упражните правата си, моля свържете се с нас на ${email}.` },
        ],
      },
      terms: {
        title: "Условия за ползване",
        updated: "Последна актуализация: 2026",
        intro:
          "Тези условия уреждат използването на уебсайта на M&K Properties. С достъпа до или използването на този уебсайт вие се съгласявате с тези условия. Този текст е предоставен с обща информационна цел и трябва да бъде прегледан от квалифициран юрист преди публикуване.",
        sections: [
          { heading: "Без съвети", body: "Информацията на този уебсайт е предоставена само с обща информационна цел и не представлява финансов, правен, данъчен или инвестиционен съвет, нито предложение или покана за покупка или продажба на актив." },
          { heading: "Без гаранции", body: "Не даваме никакви изявления или гаранции относно инвестиционни резултати, възвръщаемост или пълнотата и точността на представената информация на този уебсайт." },
          { heading: "Интелектуална собственост", body: "Цялото съдържание на този уебсайт, включително текст, оформление и брандиране, е собственост на M&K Properties, освен ако не е посочено друго, и не може да бъде възпроизвеждано без предварително писмено разрешение." },
          { heading: "Външни връзки", body: "Този уебсайт може да съдържа връзки към уебсайтове на трети страни. Ние не носим отговорност за съдържанието, политиките или практиките на уебсайтове на трети страни." },
          { heading: "Приложимо право", body: "Тези условия се уреждат от законите на Гърция, без оглед на принципите за конфликт на закони." },
          { heading: "Контакт", body: `За въпроси относно тези условия, моля свържете се с нас на ${email}.` },
        ],
      },
    },
  },

  // ==========================================================================
  // ROMANIAN
  // ==========================================================================
  ro: {
    nav: {
      home: "Acasă",
      about: "Despre noi",
      whatWeDo: "Ce facem",
      investmentApproach: "Abordare investițională",
      contact: "Contact",
      contactUs: "Contactează-ne",
    },
    hero: {
      overline: "Investiții și dezvoltare imobiliară",
      headline1: "Creăm locuințe mai bune.",
      headline2: "Construim valoare pe termen lung.",
      subtext:
        "M&K Properties este o companie de investiții și dezvoltare imobiliară cu sediul în Grecia, axată pe achiziția, modernizarea și repoziționarea proprietăților rezidențiale cu potențial ridicat.",
      primary: "Discutați o oportunitate",
      secondary: "Aflați mai multe",
    },
    snapshot: {
      title: "Profilul companiei",
      items: [
        "Cu sediul în Grecia",
        "Imobiliare rezidențiale",
        "Investiții și dezvoltare",
        "Achiziții",
        "Renovări",
        "Închirieri pe termen lung",
        "Investiții cu valoare adăugată",
      ],
    },
    about: {
      overline: "Despre noi",
      title: "Despre M&K Properties",
      p1: "M&K Properties este o companie premium de investiții și dezvoltare imobiliară cu sediul în Grecia. Identificăm proprietăți rezidențiale cu potențial ridicat, le achiziționăm, le renovăm la un standard înalt și fie le revindem, fie le păstrăm ca investiții de închiriere pe termen lung.",
      p2: "Filozofia noastră este simplă: să creăm locuințe moderne, funcționale și de înaltă calitate, generând în același timp valoare durabilă pe termen lung pentru investitori, clienți și comunitățile locale.",
      p3: "Deși accentul nostru principal este Grecia, evaluăm și oportunități pe piețe europene selectate, unde cererea rezidențială pe termen lung este susținută de fundamente solide — inclusiv stabilitatea populației, ocuparea forței de muncă, accesibilitatea și oferta limitată de locuințe.",
      quote:
        "Combinăm o selecție disciplinată a proprietăților, un design bine gândit, o execuție de calitate și o mentalitate de proprietar pe termen lung.",
    },
    why: {
      overline: "Scopul nostru",
      title: "De ce existăm",
      p1: "O parte semnificativă a imobiliarelor rezidențiale este subutilizată, învechită sau necorespunzător configurată pentru modul în care trăiesc oamenii astăzi. Aceste proprietăți rămân adesea sub potențialul lor — atât pentru oamenii care ar putea locui în ele, cât și pentru comunitățile din jur.",
      p2: "M&K Properties există pentru a acoperi acest decalaj. Achiziționăm astfel de proprietăți, le renovăm la un standard modern și funcțional și le administrăm responsabil pe termen lung — creând locuințe mai bune pentru ocupanți și valoare durabilă pentru partenerii noștri.",
    },
    whatWeDo: {
      overline: "Ce facem",
      title: "Un ciclu de investiții rezidențiale disciplinat.",
      cards: [
        { title: "Identificarea proprietăților", copy: "Identificăm proprietăți rezidențiale cu fundamente puternice de amplasare, potențial de îmbunătățire și relevanță pe termen lung pe piață." },
        { title: "Achiziție", copy: "Evaluăm și achiziționăm oportunități selectate printr-un proces de investiții disciplinat, orientat comercial." },
        { title: "Renovare și dezvoltare", copy: "Transformăm proprietățile în locuințe moderne, funcționale și de înaltă calitate printr-o renovare și dezvoltare atent planificate." },
        { title: "Revânzare și închiriere pe termen lung", copy: "Fie repoziționăm proprietățile pentru revânzare, fie le păstrăm ca investiții de închiriere pe termen lung gestionate profesionist." },
      ],
    },
    approach: {
      overline: "Abordare investițională",
      title: "Abordarea noastră investițională",
      principles: [
        { title: "Amplasarea în primul rând", copy: "Ne concentrăm pe zone cu cerere rezistentă, accesibilitate bună și potențial pe termen lung." },
        { title: "Crearea de valoare", copy: "Căutăm oportunități în care o planificare, un design și o execuție mai bune pot debloca valoare semnificativă." },
        { title: "Calitate fără excese", copy: "Prioritizăm materialele durabile, planurile eficiente și designul atemporal în locul tendințelor de scurtă durată." },
        { title: "Perspectivă pe termen lung", copy: "Fiecare decizie este evaluată prin prisma sustenabilității, a utilității și a valorii pe termen lung." },
      ],
      processLabel: "Procesul nostru",
      steps: ["Identificare", "Evaluare", "Achiziție", "Renovare", "Repoziționare", "Păstrare sau vânzare"],
    },
    focus: {
      overline: "Focus investițional",
      title: "Active rezidențiale pe care le urmărim.",
      items: [
        { title: "Apartamente urbane", copy: "Locuințe modernizate cu grijă în locații urbane consacrate și emergente." },
        { title: "Renovări rezidențiale", copy: "Interioare moderne, planuri îmbunătățite și finisaje de înaltă calitate concepute pentru viața de zi cu zi." },
        { title: "Active de închiriere pe termen lung", copy: "Proprietăți durabile și funcționale, poziționate pentru o cerere stabilă pe termen lung." },
      ],
      note: "Informațiile despre proiecte vor fi adăugate pe măsură ce portofoliul se dezvoltă.",
    },
    opportunity: {
      overline: "Oportunități",
      title: "Suntem interesați de oportunitățile potrivite",
      intro:
        "Suntem deschiși să discutăm despre proprietăți rezidențiale, oportunități în afara pieței, proiecte de reamenajare și parteneriate strategice în toată Grecia.",
      columns: [
        { title: "Proprietari", copy: "Vă gândiți să vindeți o proprietate rezidențială? Suntem interesați să evaluăm oportunitățile potrivite." },
        { title: "Agenți și brokeri", copy: "Salutăm recomandările de proprietăți cu potențial de renovare, repoziționare sau investiție." },
        { title: "Investitori și parteneri", copy: "Suntem deschiși la discuții selectate cu investitori și parteneri profesioniști care împărtășesc abordarea noastră pe termen lung." },
      ],
      cta: "Contactați M&K Properties",
    },
    trust: {
      overline: "Guvernanță",
      title: "O abordare profesională și disciplinată",
      body: "M&K Properties operează cu un accent puternic pe transparență, verificări riguroase (due diligence), alocarea responsabilă a capitalului și colaborarea profesională cu specialiști juridici, tehnici, financiari și în construcții.",
    },
    faq: {
      overline: "Întrebări frecvente",
      title: "Întrebări frecvente",
      items: [
        { q: "Ce tipuri de proprietăți achiziționați?", a: "Ne concentrăm pe imobiliare rezidențiale — în principal apartamente și clădiri rezidențiale urbane — cu fundamente solide de amplasare și un potențial clar de îmbunătățire prin renovare, planuri mai bune și repoziționare." },
        { q: "Lucrați cu agenți și brokeri?", a: "Da. Salutăm recomandările din partea agenților și brokerilor privind proprietăți rezidențiale, oportunități în afara pieței și proiecte de reamenajare care se aliniază abordării noastre pe termen lung, cu valoare adăugată." },
        { q: "Investiți în afara Greciei?", a: "Accentul nostru principal este Grecia. Evaluăm și oportunități selectate pe alte piețe europene, unde cererea rezidențială pe termen lung este susținută de fundamente solide." },
      ],
    },
    contact: {
      overline: "Contact",
      title: "Să discutăm o oportunitate",
      intro:
        "Contactați-ne cu privire la oportunități imobiliare, parteneriate sau întrebări generale de afaceri. Salutăm recomandările din partea proprietarilor, agenților, investitorilor și partenerilor profesioniști.",
      getInTouch: "Luați legătura",
      emailUs: "Trimiteți-ne un e-mail",
    },
    common: { location: "Grecia" },
    footer: {
      descriptor: "Investiții și dezvoltare imobiliară",
      privacy: "Politica de confidențialitate",
      terms: "Termeni de utilizare",
      disclaimer:
        "Informațiile de pe acest site sunt furnizate exclusiv în scop informativ general și nu constituie consultanță financiară, juridică, fiscală sau de investiții.",
      copyright: "© 2026 M&K Properties. Toate drepturile rezervate.",
    },
    legal: {
      back: "Înapoi la pagina principală",
      legalLabel: "Informații legale",
      privacy: {
        title: "Politica de confidențialitate",
        updated: "Ultima actualizare: 2026",
        intro:
          "M&K Properties („noi”, „nouă”, „al nostru”) vă respectă confidențialitatea. Această politică explică, în termeni generali, modul în care sunt gestionate informațiile trimise prin acest site. Este furnizată în scop informativ general și ar trebui revizuită de un profesionist juridic calificat înainte de publicare.",
        sections: [
          { heading: "Ce informații colectăm", body: "Când utilizați canalele noastre de contact, colectăm datele pe care alegeți să le furnizați, cum ar fi numele, compania, adresa de e-mail, numărul de telefon, motivul contactării și mesajul." },
          { heading: "Cum utilizăm informațiile", body: "Informațiile dvs. sunt utilizate exclusiv pentru a răspunde solicitării dvs. și pentru a comunica cu dvs. privind oportunități imobiliare, parteneriate sau chestiuni generale de afaceri. Nu vindem informațiile dvs. către terți." },
          { heading: "Păstrarea datelor", body: "Păstrăm informațiile din solicitări doar atât timp cât este necesar pentru a răspunde și a gestiona cererea dvs., după care pot fi șterse la cerere." },
          { heading: "Drepturile dvs.", body: "Puteți solicita accesul, corectarea sau ștergerea informațiilor personale pe care ni le-ați furnizat în orice moment, contactându-ne." },
          { heading: "Contact", body: `Pentru orice întrebări legate de această politică sau pentru a vă exercita drepturile, vă rugăm să ne contactați la ${email}.` },
        ],
      },
      terms: {
        title: "Termeni de utilizare",
        updated: "Ultima actualizare: 2026",
        intro:
          "Acești termeni reglementează utilizarea site-ului M&K Properties. Prin accesarea sau utilizarea acestui site, sunteți de acord cu acești termeni. Acest text este furnizat în scop informativ general și ar trebui revizuit de un profesionist juridic calificat înainte de publicare.",
        sections: [
          { heading: "Fără consultanță", body: "Informațiile de pe acest site sunt furnizate exclusiv în scop informativ general și nu constituie consultanță financiară, juridică, fiscală sau de investiții, nici o ofertă sau solicitare de a cumpăra sau vinde vreun activ." },
          { heading: "Fără garanții", body: "Nu oferim nicio declarație sau garanție privind rezultatele investiționale, randamentele sau caracterul complet și exactitatea informațiilor prezentate pe acest site." },
          { heading: "Proprietate intelectuală", body: "Tot conținutul acestui site, inclusiv textul, aspectul și brandingul, este proprietatea M&K Properties, cu excepția cazului în care se specifică altfel, și nu poate fi reprodus fără permisiune scrisă prealabilă." },
          { heading: "Linkuri externe", body: "Acest site poate conține linkuri către site-uri terțe. Nu suntem responsabili pentru conținutul, politicile sau practicile site-urilor terțe." },
          { heading: "Legea aplicabilă", body: "Acești termeni sunt guvernați de legile Greciei, fără a ține cont de principiile privind conflictul de legi." },
          { heading: "Contact", body: `Pentru întrebări privind acești termeni, vă rugăm să ne contactați la ${email}.` },
        ],
      },
    },
  },

  // ==========================================================================
  // GREEK
  // ==========================================================================
  el: {
    nav: {
      home: "Αρχική",
      about: "Σχετικά",
      whatWeDo: "Τι κάνουμε",
      investmentApproach: "Επενδυτική προσέγγιση",
      contact: "Επικοινωνία",
      contactUs: "Επικοινωνήστε",
    },
    hero: {
      overline: "Επένδυση & Ανάπτυξη Ακινήτων",
      headline1: "Δημιουργούμε καλύτερα σπίτια.",
      headline2: "Χτίζουμε μακροπρόθεσμη αξία.",
      subtext:
        "Η M&K Properties είναι εταιρεία επένδυσης και ανάπτυξης ακινήτων με έδρα την Ελλάδα, με επίκεντρο την απόκτηση, αναβάθμιση και επανατοποθέτηση οικιστικών ακινήτων υψηλού δυναμικού.",
      primary: "Συζητήστε μια ευκαιρία",
      secondary: "Μάθετε περισσότερα",
    },
    snapshot: {
      title: "Προφίλ εταιρείας",
      items: [
        "Έδρα στην Ελλάδα",
        "Οικιστικά ακίνητα",
        "Επένδυση & Ανάπτυξη",
        "Αποκτήσεις",
        "Ανακαινίσεις",
        "Μακροχρόνιες μισθώσεις",
        "Επενδύσεις προστιθέμενης αξίας",
      ],
    },
    about: {
      overline: "Σχετικά",
      title: "Σχετικά με την M&K Properties",
      p1: "Η M&K Properties είναι εταιρεία επένδυσης και ανάπτυξης ακινήτων υψηλών προδιαγραφών με έδρα την Ελλάδα. Εντοπίζουμε οικιστικά ακίνητα υψηλού δυναμικού, τα αποκτούμε, τα ανακαινίζουμε σε υψηλό επίπεδο και είτε τα μεταπωλούμε είτε τα διατηρούμε ως μακροπρόθεσμες επενδύσεις προς ενοικίαση.",
      p2: "Η φιλοσοφία μας είναι απλή: να δημιουργούμε σύγχρονα, λειτουργικά και υψηλής ποιότητας σπίτια, δημιουργώντας παράλληλα βιώσιμη μακροπρόθεσμη αξία για επενδυτές, πελάτες και τοπικές κοινότητες.",
      p3: "Αν και το κύριο επίκεντρό μας είναι η Ελλάδα, αξιολογούμε επίσης ευκαιρίες σε επιλεγμένες ευρωπαϊκές αγορές, όπου η μακροπρόθεσμη οικιστική ζήτηση στηρίζεται από ισχυρά θεμελιώδη μεγέθη — συμπεριλαμβανομένης της σταθερότητας του πληθυσμού, της απασχόλησης, της προσβασιμότητας και της περιορισμένης προσφοράς κατοικιών.",
      quote:
        "Συνδυάζουμε πειθαρχημένη επιλογή ακινήτων, προσεγμένο σχεδιασμό, ποιοτική υλοποίηση και μακροπρόθεσμη νοοτροπία ιδιοκτησίας.",
    },
    why: {
      overline: "Ο σκοπός μας",
      title: "Γιατί υπάρχουμε",
      p1: "Ένα σημαντικό μέρος των οικιστικών ακινήτων είναι υποαξιοποιημένο, απαρχαιωμένο ή ακατάλληλα διαμορφωμένο για τον τρόπο που ζουν οι άνθρωποι σήμερα. Τα ακίνητα αυτά συχνά παραμένουν κάτω από το δυναμικό τους — τόσο για τους ανθρώπους που θα μπορούσαν να ζουν σε αυτά όσο και για τις γύρω κοινότητες.",
      p2: "Η M&K Properties υπάρχει για να γεφυρώσει αυτό το κενό. Αποκτούμε τέτοια ακίνητα, τα ανακαινίζουμε σε σύγχρονο και λειτουργικό επίπεδο και τα διαχειριζόμαστε υπεύθυνα μακροπρόθεσμα — δημιουργώντας καλύτερα σπίτια για τους ενοίκους και διαρκή, βιώσιμη αξία για τους συνεργάτες μας.",
    },
    whatWeDo: {
      overline: "Τι κάνουμε",
      title: "Ένας πειθαρχημένος κύκλος οικιστικών επενδύσεων.",
      cards: [
        { title: "Εντοπισμός ακινήτων", copy: "Εντοπίζουμε οικιστικά ακίνητα με ισχυρά θεμελιώδη τοποθεσίας, δυναμικό βελτίωσης και μακροπρόθεσμη σημασία στην αγορά." },
        { title: "Απόκτηση", copy: "Αξιολογούμε και αποκτούμε επιλεγμένες ευκαιρίες μέσω μιας πειθαρχημένης, εμπορικά προσανατολισμένης επενδυτικής διαδικασίας." },
        { title: "Ανακαίνιση & Ανάπτυξη", copy: "Μετατρέπουμε τα ακίνητα σε σύγχρονα, λειτουργικά και υψηλής ποιότητας σπίτια μέσω προσεκτικά σχεδιασμένης ανακαίνισης και ανάπτυξης." },
        { title: "Μεταπώληση & Μακροχρόνια μίσθωση", copy: "Είτε επανατοποθετούμε τα ακίνητα για μεταπώληση είτε τα διατηρούμε ως επαγγελματικά διαχειριζόμενες μακροπρόθεσμες επενδύσεις προς ενοικίαση." },
      ],
    },
    approach: {
      overline: "Επενδυτική προσέγγιση",
      title: "Η επενδυτική μας προσέγγιση",
      principles: [
        { title: "Η τοποθεσία πρώτα", copy: "Εστιάζουμε σε περιοχές με ανθεκτική ζήτηση, καλή προσβασιμότητα και μακροπρόθεσμο δυναμικό." },
        { title: "Δημιουργία αξίας", copy: "Αναζητούμε ευκαιρίες όπου ο καλύτερος σχεδιασμός, η μελέτη και η υλοποίηση μπορούν να απελευθερώσουν ουσιαστική αξία." },
        { title: "Ποιότητα χωρίς υπερβολές", copy: "Δίνουμε προτεραιότητα σε ανθεκτικά υλικά, αποδοτικές κατόψεις και διαχρονικό σχεδιασμό αντί για βραχύβιες τάσεις." },
        { title: "Μακροπρόθεσμη προοπτική", copy: "Κάθε απόφαση αξιολογείται υπό το πρίσμα της βιωσιμότητας, της χρηστικότητας και της μακροπρόθεσμης αξίας." },
      ],
      processLabel: "Η διαδικασία μας",
      steps: ["Εντοπισμός", "Αξιολόγηση", "Απόκτηση", "Ανακαίνιση", "Επανατοποθέτηση", "Διακράτηση ή Πώληση"],
    },
    focus: {
      overline: "Επενδυτικό επίκεντρο",
      title: "Οικιστικά περιουσιακά στοιχεία που επιδιώκουμε.",
      items: [
        { title: "Αστικά διαμερίσματα", copy: "Προσεκτικά αναβαθμισμένα σπίτια σε καθιερωμένες και αναδυόμενες αστικές περιοχές." },
        { title: "Οικιστικές ανακαινίσεις", copy: "Σύγχρονοι εσωτερικοί χώροι, βελτιωμένες κατόψεις και υψηλής ποιότητας φινιρίσματα σχεδιασμένα για την καθημερινή ζωή." },
        { title: "Ακίνητα μακροχρόνιας μίσθωσης", copy: "Ανθεκτικά, λειτουργικά ακίνητα, τοποθετημένα για σταθερή μακροπρόθεσμη ζήτηση." },
      ],
      note: "Πληροφορίες για τα έργα θα προστίθενται καθώς αναπτύσσεται το χαρτοφυλάκιο.",
    },
    opportunity: {
      overline: "Ευκαιρίες",
      title: "Μας ενδιαφέρουν οι σωστές ευκαιρίες",
      intro:
        "Είμαστε ανοιχτοί να συζητήσουμε οικιστικά ακίνητα, ευκαιρίες εκτός αγοράς, έργα ανάπλασης και στρατηγικές συνεργασίες σε όλη την Ελλάδα.",
      columns: [
        { title: "Ιδιοκτήτες ακινήτων", copy: "Σκέφτεστε να πουλήσετε ένα οικιστικό ακίνητο; Ενδιαφερόμαστε να αξιολογήσουμε κατάλληλες ευκαιρίες." },
        { title: "Μεσίτες & Πράκτορες", copy: "Καλωσορίζουμε συστάσεις για ακίνητα με δυναμικό ανακαίνισης, επανατοποθέτησης ή επένδυσης." },
        { title: "Επενδυτές & Συνεργάτες", copy: "Είμαστε ανοιχτοί σε επιλεγμένες συζητήσεις με επενδυτές και επαγγελματίες συνεργάτες που συμμερίζονται τη μακροπρόθεσμη προσέγγισή μας." },
      ],
      cta: "Επικοινωνήστε με την M&K Properties",
    },
    trust: {
      overline: "Διακυβέρνηση",
      title: "Μια επαγγελματική, πειθαρχημένη προσέγγιση",
      body: "Η M&K Properties λειτουργεί με ισχυρή έμφαση στη διαφάνεια, τον προσεκτικό δέοντα έλεγχο (due diligence), την υπεύθυνη κατανομή κεφαλαίου και την επαγγελματική συνεργασία με νομικούς, τεχνικούς, οικονομικούς και κατασκευαστικούς ειδικούς.",
    },
    faq: {
      overline: "Συχνές ερωτήσεις",
      title: "Συχνές ερωτήσεις",
      items: [
        { q: "Τι είδους ακίνητα αποκτάτε;", a: "Εστιάζουμε σε οικιστικά ακίνητα — κυρίως διαμερίσματα και αστικά οικιστικά κτίρια — με στέρεα θεμελιώδη τοποθεσίας και σαφές δυναμικό βελτίωσης μέσω ανακαίνισης, καλύτερων κατόψεων και επανατοποθέτησης." },
        { q: "Συνεργάζεστε με μεσίτες και πράκτορες;", a: "Ναι. Καλωσορίζουμε συστάσεις από μεσίτες και πράκτορες για οικιστικά ακίνητα, ευκαιρίες εκτός αγοράς και έργα ανάπλασης που ευθυγραμμίζονται με τη μακροπρόθεσμη προσέγγισή μας προστιθέμενης αξίας." },
        { q: "Επενδύετε εκτός Ελλάδας;", a: "Το κύριο επίκεντρό μας είναι η Ελλάδα. Αξιολογούμε επίσης επιλεγμένες ευκαιρίες σε άλλες ευρωπαϊκές αγορές, όπου η μακροπρόθεσμη οικιστική ζήτηση στηρίζεται από ισχυρά θεμελιώδη μεγέθη." },
      ],
    },
    contact: {
      overline: "Επικοινωνία",
      title: "Ας συζητήσουμε μια ευκαιρία",
      intro:
        "Επικοινωνήστε μαζί μας σχετικά με ευκαιρίες ακινήτων, συνεργασίες ή γενικά επιχειρηματικά ερωτήματα. Καλωσορίζουμε συστάσεις από ιδιοκτήτες ακινήτων, μεσίτες, επενδυτές και επαγγελματίες συνεργάτες.",
      getInTouch: "Επικοινωνήστε μαζί μας",
      emailUs: "Στείλτε μας email",
    },
    common: { location: "Ελλάδα" },
    footer: {
      descriptor: "Επένδυση & Ανάπτυξη Ακινήτων",
      privacy: "Πολιτική Απορρήτου",
      terms: "Όροι Χρήσης",
      disclaimer:
        "Οι πληροφορίες σε αυτόν τον ιστότοπο παρέχονται αποκλειστικά για γενικούς ενημερωτικούς σκοπούς και δεν αποτελούν χρηματοοικονομική, νομική, φορολογική ή επενδυτική συμβουλή.",
      copyright: "© 2026 M&K Properties. Με την επιφύλαξη παντός δικαιώματος.",
    },
    legal: {
      back: "Επιστροφή στην αρχική",
      legalLabel: "Νομικά",
      privacy: {
        title: "Πολιτική Απορρήτου",
        updated: "Τελευταία ενημέρωση: 2026",
        intro:
          "Η M&K Properties («εμείς», «εμάς», «μας») σέβεται το απόρρητό σας. Η παρούσα πολιτική εξηγεί, σε γενικές γραμμές, πώς διαχειριζόμαστε τις πληροφορίες που υποβάλλονται μέσω αυτού του ιστότοπου. Παρέχεται για γενικούς ενημερωτικούς σκοπούς και θα πρέπει να ελεγχθεί από εξειδικευμένο νομικό επαγγελματία πριν από τη δημοσίευση.",
        sections: [
          { heading: "Ποιες πληροφορίες συλλέγουμε", body: "Όταν χρησιμοποιείτε τα κανάλια επικοινωνίας μας, συλλέγουμε τα στοιχεία που επιλέγετε να παρέχετε, όπως το όνομά σας, την εταιρεία, τη διεύθυνση email, τον αριθμό τηλεφώνου, τον λόγο επικοινωνίας και το μήνυμά σας." },
          { heading: "Πώς χρησιμοποιούμε τις πληροφορίες σας", body: "Οι πληροφορίες σας χρησιμοποιούνται αποκλειστικά για να απαντήσουμε στο αίτημά σας και να επικοινωνήσουμε μαζί σας σχετικά με ευκαιρίες ακινήτων, συνεργασίες ή γενικά επιχειρηματικά θέματα. Δεν πουλάμε τις πληροφορίες σας σε τρίτους." },
          { heading: "Διατήρηση δεδομένων", body: "Διατηρούμε τις πληροφορίες των αιτημάτων μόνο για όσο διάστημα είναι απαραίτητο για να απαντήσουμε και να διαχειριστούμε το αίτημά σας, μετά το οποίο μπορούν να διαγραφούν κατόπιν αιτήματος." },
          { heading: "Τα δικαιώματά σας", body: "Μπορείτε να ζητήσετε πρόσβαση, διόρθωση ή διαγραφή των προσωπικών πληροφοριών που μας έχετε παράσχει ανά πάσα στιγμή, επικοινωνώντας μαζί μας." },
          { heading: "Επικοινωνία", body: `Για οποιεσδήποτε ερωτήσεις σχετικά με την παρούσα πολιτική ή για να ασκήσετε τα δικαιώματά σας, επικοινωνήστε μαζί μας στο ${email}.` },
        ],
      },
      terms: {
        title: "Όροι Χρήσης",
        updated: "Τελευταία ενημέρωση: 2026",
        intro:
          "Οι παρόντες όροι διέπουν τη χρήση του ιστότοπου της M&K Properties. Με την πρόσβαση ή τη χρήση αυτού του ιστότοπου, συμφωνείτε με τους παρόντες όρους. Το κείμενο αυτό παρέχεται για γενικούς ενημερωτικούς σκοπούς και θα πρέπει να ελεγχθεί από εξειδικευμένο νομικό επαγγελματία πριν από τη δημοσίευση.",
        sections: [
          { heading: "Καμία συμβουλή", body: "Οι πληροφορίες σε αυτόν τον ιστότοπο παρέχονται αποκλειστικά για γενικούς ενημερωτικούς σκοπούς και δεν αποτελούν χρηματοοικονομική, νομική, φορολογική ή επενδυτική συμβουλή, ούτε προσφορά ή πρόσκληση για αγορά ή πώληση οποιουδήποτε περιουσιακού στοιχείου." },
          { heading: "Καμία εγγύηση", body: "Δεν παρέχουμε καμία δήλωση ή εγγύηση σχετικά με τα επενδυτικά αποτελέσματα, τις αποδόσεις ή την πληρότητα και την ακρίβεια των πληροφοριών που παρουσιάζονται σε αυτόν τον ιστότοπο." },
          { heading: "Πνευματική ιδιοκτησία", body: "Όλο το περιεχόμενο αυτού του ιστότοπου, συμπεριλαμβανομένου του κειμένου, της διάταξης και της επωνυμίας, αποτελεί ιδιοκτησία της M&K Properties, εκτός εάν αναφέρεται διαφορετικά, και δεν επιτρέπεται η αναπαραγωγή του χωρίς προηγούμενη γραπτή άδεια." },
          { heading: "Εξωτερικοί σύνδεσμοι", body: "Αυτός ο ιστότοπος ενδέχεται να περιέχει συνδέσμους προς ιστότοπους τρίτων. Δεν φέρουμε ευθύνη για το περιεχόμενο, τις πολιτικές ή τις πρακτικές ιστότοπων τρίτων." },
          { heading: "Εφαρμοστέο δίκαιο", body: "Οι παρόντες όροι διέπονται από τους νόμους της Ελλάδας, ανεξαρτήτως των αρχών περί σύγκρουσης νόμων." },
          { heading: "Επικοινωνία", body: `Για ερωτήσεις σχετικά με τους παρόντες όρους, επικοινωνήστε μαζί μας στο ${email}.` },
        ],
      },
    },
  },
};
